import { useState } from 'react';
import { Box, Button, IconButton, ToggleButton, ToggleButtonGroup, Select, MenuItem, FormControl, Checkbox, Popover, Paper, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { EditRounded, AddRounded, SaveRounded, CancelRounded, Close, Style as ColorwayIcon, Save as SaveIcon } from '@mui/icons-material';
import { useChartData } from './ChartDataProvider';
import { HollowSquareIcon, DiagonalDiamondIcon } from './drawingModeIcons';
import { iconOptions, renderIconSvg } from '../iconsSvg';
import ColorwaySelector from '../../../Colorway/ColorwaySelector';
import { useUpdatePropertyMutation } from '../../../store/docApi';
import { generateEntityId } from '../../../utils';

// Helper function to render icons with forced black color for dropdowns
const renderDropdownIconSvg = (icon, isSolid) => {
  const option = iconOptions.find((opt) => opt.value === icon);
  if (!option?.draw) return null;
  
  const strokeColor = '#000000';
  const fillColor = isSolid ? '#000000' : 'none';
  // Use larger size for heart icon specifically to make it bigger
  const size = icon === 'heart' ? 8 : 6;
  return option.draw(5, 5, size, strokeColor, fillColor);
};

const ColorPickerPanel = () => {
  const [isEdit, setIsEdit] = useState(false);
  const [editColorway, setEditColorway] = useState({ chromas: [] });
  const [editNoStitchColor, setEditNoStitchColor] = useState('#e0e0e0');
  const [colorwayAnchorEl, setColorwayAnchorEl] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [colorwayName, setColorwayName] = useState('');
  
  const [updateProperty] = useUpdatePropertyMutation();
  
  const { 
    item,
    colorway,
    currentColor, 
    settings, 
    drawingMode,
    actions 
  } = useChartData();

  const noStitchColor = settings?.noStitchColor || '#e0e0e0';

  const startEdit = () => {
    setEditColorway({
      chromas: colorway.chromas.map(chroma => ({
        color: chroma.color,
        icon: chroma.icon || '',
        isSolid: chroma.isSolid || false
      }))
    });
    setEditNoStitchColor(noStitchColor);
    setIsEdit(true);
  };

  const onCancel = () => {
    setEditColorway({ chromas: [] });
    setEditNoStitchColor('#e0e0e0');
    setIsEdit(false);
  };

  const onSave = () => {
    // Update the unified colorway
    actions.updateColorway(editColorway);
    actions.updateSettings({ noStitchColor: editNoStitchColor });
    setEditColorway({ chromas: [] });
    setEditNoStitchColor('#e0e0e0');
    setIsEdit(false);
  };

  const handleColorwayClick = (event) => {
    setColorwayAnchorEl(event.currentTarget);
  };

  const handleColorwayClose = () => {
    setColorwayAnchorEl(null);
  };

  const handleColorwaySelect = (selectedColorway) => {
    actions.updateColorway(selectedColorway);
    handleColorwayClose();
  };

  const handleSaveAsColorway = () => {
    setSaveDialogOpen(true);
  };

  const handleSaveDialogClose = () => {
    setSaveDialogOpen(false);
    setColorwayName('');
  };

  const handleSaveColorway = async () => {
    if (!colorwayName.trim() || !item) return;
    
    const colorwayId = generateEntityId('colorway');
    // Use editColorway if in edit mode, otherwise use current colorway
    const sourceColorway = isEdit ? editColorway : colorway;
    const newColorway = {
      id: colorwayId,
      name: colorwayName.trim(),
      chromas: sourceColorway.chromas.map(chroma => ({
        color: chroma.color,
        icon: chroma.icon || '',
        isSolid: chroma.isSolid || false
      }))
    };

    try {
      await updateProperty({
        label: item.label,
        pk: item.pk,
        id: item.id,
        name: `colorways/${colorwayId}`,
        value: newColorway
      });
      handleSaveDialogClose();
    } catch (error) {
      console.error('Failed to save colorway:', error);
    }
  };

  if (isEdit) {
    return (
      <Box>
        {/* No-Stitch Color Setting */}
        <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <Box sx={{ mb: 1, fontSize: '0.875rem', fontWeight: 'bold' }}>
            No-Stitch Color
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <input 
              type="color" 
              value={editNoStitchColor} 
              onChange={(e) => setEditNoStitchColor(e.target.value)}
              style={{ 
                width: '48px', 
                height: '32px', 
                border: '1px solid #ccc', 
                borderRadius: '4px', 
                cursor: 'pointer' 
              }}
            />
            <Box
              sx={{
                backgroundColor: editNoStitchColor,
                width: 32,
                height: 32,
                borderRadius: 1,
                border: '1px solid #ccc',
                position: 'relative'
              }}
            >
              {/* Visual indicator for no stitch - diagonal line */}
              <Box
                sx={{
                  position: 'absolute',
                  width: '70%',
                  height: '2px',
                  backgroundColor: 'red',
                  transform: 'rotate(45deg)',
                  top: '50%',
                  left: '50%',
                  transformOrigin: 'center',
                  marginTop: '-1px',
                  marginLeft: '-35%'
                }}
              />
            </Box>
          </Box>
        </Box>
        
        {/* Color Palette */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: 1 }}>
          {/* Solid checkbox header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 0.5 }}>
            <Box sx={{ width: '40px' }}></Box> {/* Spacer for color picker */}
            <Box sx={{ minWidth: 90 }}></Box> {/* Spacer for icon dropdown */}
            <Box sx={{ 
              fontSize: '0.7rem', 
              fontWeight: 'bold',
              color: 'text.secondary',
              textAlign: 'center',
              minWidth: '40px'
            }}>
              Solid
            </Box>
          </Box>
          {editColorway.chromas.map((chroma, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '10px',
                p: 0.5,
                border: '1px solid #e0e0e0',
                borderRadius: 1
              }}
            >
              <input 
                type="color" 
                value={chroma.color} 
                size="small"
                style={{ width: '40px', height: '28px', border: 'none', cursor: 'pointer' }}
                onChange={(e) => {
                  const newColorway = { ...editColorway };
                  newColorway.chromas[index].color = e.target.value;
                  setEditColorway(newColorway);
                }}
              />
              <FormControl size="small" sx={{ minWidth: 90 }}>
                <Select
                  value={chroma.icon || ''}
                  displayEmpty
                  sx={{ 
                    fontSize: '0.8rem',
                    '& .MuiSelect-select': { 
                      py: 0.5,
                      fontSize: '0.8rem'
                    }
                  }}
                  renderValue={(selected) => {
                    if (!selected || selected === '') {
                      return <span style={{ color: '#bbb', fontSize: '0.8rem', fontStyle: 'italic' }}>Select icon...</span>;
                    }
                    const option = iconOptions.find(opt => opt.value === selected);
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <svg width="14" height="14" viewBox="0 0 10 10">
                          {renderDropdownIconSvg(selected, chroma.isSolid)}
                        </svg>
                        <span>{option?.label}</span>
                      </Box>
                    );
                  }}
                  onChange={(e) => {
                    const newColorway = { ...editColorway };
                    newColorway.chromas[index].icon = e.target.value;
                    setEditColorway(newColorway);
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: '0.8rem', py: 0.5, color: '#999', fontStyle: 'italic' }}>
                    <span>No icon</span>
                  </MenuItem>
                  {iconOptions.filter(option => option.value !== '').map((option) => (
                    <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <svg width="14" height="14" viewBox="0 0 10 10">
                          {renderDropdownIconSvg(option.value, chroma.isSolid)}
                        </svg>
                        <span>{option.label}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', justifyContent: 'center', minWidth: '40px' }}>
                <Checkbox
                  checked={chroma.isSolid || false}
                  onChange={(e) => {
                    const newColorway = { ...editColorway };
                    newColorway.chromas[index].isSolid = e.target.checked;
                    setEditColorway(newColorway);
                  }}
                  size="small"
                  sx={{ 
                    opacity: chroma.icon ? 1 : 0.5,
                    padding: '2px'
                  }}
                />
              </Box>
              <IconButton
                size="small"
                sx={{ padding: '2px', ml: 0 }}
                onClick={() => {
                  const newColorway = { ...editColorway };
                  newColorway.chromas.splice(index, 1);
                  setEditColorway(newColorway);
                }}
              >
                <Close sx={{ fontSize: '16px' }} />
              </IconButton>
            </Box>
          ))}
          <Button size="small" variant="outlined" onClick={() => {
            const newColorway = { ...editColorway };
            newColorway.chromas.push({ color: '#ffffff', icon: '', isSolid: false });
            setEditColorway(newColorway);
          }}>
            <AddRounded />
          </Button>
        </Box>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button size="small" startIcon={<SaveRounded />} onClick={onSave} variant="contained">
            Save Changes
          </Button>
          <Button size="small" startIcon={<SaveIcon />} onClick={handleSaveAsColorway} variant="outlined">
            Save as New Colorway
          </Button>
          <Button size="small" startIcon={<CancelRounded />} onClick={onCancel} variant="outlined">
            Cancel
          </Button>
        </Box>
      </Box>
    );
  } else {
    return (
      <Box>
        {/* Colorway Selection Button */}
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleColorwayClick}
            startIcon={<ColorwayIcon />}
            fullWidth
            sx={{ justifyContent: 'flex-start' }}
          >
            Colorways
          </Button>
        </Box>
        
        {/* Save as Colorway Button */}
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleSaveAsColorway}
            startIcon={<SaveIcon />}
            fullWidth
            sx={{ justifyContent: 'flex-start' }}
          >
            Save as Colorway
          </Button>
        </Box>
        
        {/* Show Icons Setting */}
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox 
                checked={settings.showIcons} 
                onChange={(e) => actions.updateSettings({ showIcons: e.target.checked })} 
                size="small" 
              />
            }
            label="Show icons"
            sx={{ fontSize: '0.875rem' }}
          />
        </Box>
        
        {/* Drawing Mode Toggle */}
        <Box sx={{ mb: 2 }}>
          <ToggleButtonGroup
            value={drawingMode}
            exclusive
            onChange={(event, newMode) => {
              if (newMode !== null) {
                actions.setDrawingMode(newMode);
              }
            }}
            size="small"
            fullWidth
          >
            <ToggleButton value="single" aria-label="single cell">
              <HollowSquareIcon size={16} />
              <Box sx={{ ml: 0.5, fontSize: '0.75rem' }}>Single</Box>
            </ToggleButton>
            <ToggleButton value="fill" aria-label="fill area">
              <DiagonalDiamondIcon size={16} />
              <Box sx={{ ml: 0.5, fontSize: '0.75rem' }}>Fill</Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
          {/* No Stitch Option */}
          <Box
            onClick={() => actions.setCurrentColor('no-stitch')}
            sx={{
              backgroundColor: noStitchColor,
              borderWidth: currentColor === 'no-stitch' ? 3 : 1,
              borderColor: currentColor === 'no-stitch' ? 'black' : 'gray',
              borderStyle: 'solid',
              borderRadius: 1,
              cursor: 'pointer',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}
          >
            {/* Visual indicator for no stitch - diagonal line */}
            <Box
              sx={{
                position: 'absolute',
                width: '70%',
                height: '2px',
                backgroundColor: 'red',
                transform: 'rotate(45deg)',
                top: '50%',
                left: '50%',
                transformOrigin: 'center',
                marginTop: '-1px',
                marginLeft: '-35%'
              }}
            />
            {currentColor === 'no-stitch' && (
              <Box sx={{ 
                color: 'black',
                fontSize: '10px',
                fontWeight: 'bold',
                position: 'absolute',
                bottom: '2px',
                right: '2px'
              }}>
                ∅
              </Box>
            )}
          </Box>
          {colorway.chromas.map((chroma, index) => (
            <Box
              key={index}
              onClick={() => actions.setCurrentColor(index)}
              sx={{
                backgroundColor: chroma.color,
                borderWidth: currentColor === index ? 3 : 1,
                borderColor: currentColor === index ? 'black' : 'gray',
                borderStyle: 'solid',
                borderRadius: 1,
                cursor: 'pointer',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
              }}
            >
              {/* Icon display */}
              {chroma.icon && (
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                  <svg width="20" height="20" viewBox="0 0 10 10">
                    {renderIconSvg(chroma.icon, chroma.color, chroma.isSolid)}
                  </svg>
                </Box>
              )}
              {/* Color number indicator */}
              {currentColor === index && (
                <Box sx={{ 
                  color: chroma.color === '#ffffff' || chroma.color === '#FFFFFF' ? 'black' : 'white',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  position: 'absolute',
                  bottom: '2px',
                  right: '2px',
                  backgroundColor: 'rgba(0,0,0,0.3)',
                  borderRadius: '50%',
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {index + 1}
                </Box>
              )}
            </Box>
          ))}
          
          <IconButton size="small" onClick={startEdit} sx={{ mt: 1 }}>
            <EditRounded />
          </IconButton>
        </Box>
        
        {/* Colorway Selection Popover */}
        <Popover
          open={Boolean(colorwayAnchorEl)}
          anchorEl={colorwayAnchorEl}
          onClose={handleColorwayClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: '300px',
              maxWidth: '500px'
            }
          }}
        >
          <Paper sx={{ p: 2 }}>
            <ColorwaySelector item={item} onSelect={handleColorwaySelect} />
          </Paper>
        </Popover>
        
        {/* Save Colorway Dialog */}
        <Dialog open={saveDialogOpen} onClose={handleSaveDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>Save as Colorway</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Colorway Name"
              fullWidth
              variant="outlined"
              value={colorwayName}
              onChange={(e) => setColorwayName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSaveColorway();
                }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleSaveDialogClose}>Cancel</Button>
            <Button 
              onClick={handleSaveColorway} 
              variant="contained"
              disabled={!colorwayName.trim()}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
};

export default ColorPickerPanel;
